import { useUser } from "@clerk/clerk-react";
import Container from "components/general/Container";
import Section from "components/general/Section";
import { Box, H2, Image } from "components/ui";
import { ParagraphSmall, Typography } from "components/ui/Typography";
import Constants from "expo-constants";
import React, { ReactNode, useEffect } from "react";

// Use let to allow conditional initialization
let Bugsnag: typeof import("@bugsnag/js").default | undefined;
let ErrorBoundary: React.ComponentType<{
	children: ReactNode;
	FallbackComponent: React.ComponentType;
}> | null = null;

// Initialize Bugsnag if in production
if (typeof window !== "undefined" && process.env.NODE_ENV === "production") {
	Bugsnag = require("@bugsnag/js").default;
	const BugsnagPluginReact = require("@bugsnag/plugin-react").default;

	Bugsnag.start({
		apiKey: "26cc5dbf23c61d1b9699e1d08ed083d9",
		plugins: [new BugsnagPluginReact()],
		appVersion: Constants.expoConfig?.version,
	});

	const plugin = Bugsnag.getPlugin("react");
	if (plugin) {
		ErrorBoundary = plugin.createErrorBoundary(React);
	}
}

const ErrorView = (props: { error: Error }) => {
	return (
		<Container sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
			<Section borderWidth={1} borderColor="$neutral100" alignItems="center" justifyContent="center" borderRadius={8} gap={12}>
				<Image resizeMode="contain" source={require("assets/undraw_notify_rnwe.svg")} alt="Abco: The Power to Clean 1" style={{ width: 300, height: 300 }} />
				<H2 color="$primarylight">Oops! Something went wrong.</H2>
				<Typography color="$neutral600">Please refresh the page.</Typography>
				{props.error.message && (
					<Box marginTop={24} width="100%">
						<Typography.ParagraphSmall weight="bold">Error details:</Typography.ParagraphSmall>
						<ParagraphSmall sx={{ fontFamily: "calibri" }} color="$neutral600">
							{props.error.message}
						</ParagraphSmall>
					</Box>
				)}
			</Section>
		</Container>
	);
};

type BugsnagContainerProps = {
	children: ReactNode;
};

export const BugsnagContainer: React.FC<BugsnagContainerProps> = ({ children }) => {
	const { user } = useUser();

	useEffect(() => {
		// Ensure Bugsnag is actually initialized before using it
		if (Bugsnag && user) {
			Bugsnag.setUser(user.id, user.emailAddresses[0]?.emailAddress, user.fullName || user.username);
		}
	}, [user]);

	if (ErrorBoundary) {
		return <ErrorBoundary FallbackComponent={ErrorView}>{children}</ErrorBoundary>;
	}

	return <>{children}</>;
};
